import { createContext, useContext } from "react";

import type { PMIssue, SchedulePreview } from "@/domains/pm/types";
import type { DatePatch } from "@/domains/pm/hooks/useScheduleChange";

export interface ScheduleChangeApi {
  /** Ask for a date change, confirming with the user when it moves others. */
  request: (issue: PMIssue, patch: DatePatch) => Promise<void>;
  /** Can moving this issue move or upset anything else? Answered from the
   *  loaded dependency index, so it costs no round trip. */
  hasDependents: (issueId: number) => boolean;
  /** Write a date and cascade it without asking. Used by bulk edits, which
   *  report what happened afterwards rather than prompting per issue. */
  applyWithCascade: (
    issue: PMIssue,
    start: string | null,
    due: string | null
  ) => Promise<SchedulePreview | null>;
}

export const ScheduleChangeContext = createContext<ScheduleChangeApi | null>(null);

/**
 * The cascade, if the surrounding page provides one.
 *
 * Returns null outside a provider, and callers must handle that by writing the
 * date themselves — deliberately explicit rather than a silent no-cascade
 * default, because "the date saved but the schedule was not recalculated" is
 * the exact failure this feature exists to prevent. It should be visible in
 * the calling code, not buried in a hook.
 */
export function useScheduleChangeApi(): ScheduleChangeApi | null {
  return useContext(ScheduleChangeContext);
}
